import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Col, Row } from 'reactstrap';
import { createUseStyles } from 'react-jss';
import { Trans } from 'react-i18next';
import moment from 'moment';
import useCountDown from 'react-countdown-hook';
import i18n from 'i18n';
import { Link, useHistory } from 'react-router-dom';
import { useCakeDispatch } from 'app/store';
import { storage } from 'storage/sessionStorage';
import { useCakeSelector } from 'app/rootReducer';
import useRedeem from 'voucher/hooks/useRedeem';
import {
  isValidEmail,
  isEmpty,
} from 'utils';
import config from 'config/app';
import sdk from 'sdk';
import { userTokenInfoStorage } from 'storage';
import {
  ClickableElement,
  InvisibleRecaptchaV2,
} from 'views/components';
import { reloadUser } from 'user/userSlice';
import { reloadBalances } from 'wallet/walletSlice';
import Button from 'common/components/Button/Button';
import { LoginStyles } from 'views/Pages/Authentication/Authentication.styles';
import EmailPasswordInput from 'views/components/Login/EmailPasswordInput';
import ErrorPage from 'views/components/Login/ErrorPage';
import OTPInput from 'views/components/Login/OTPInput';
import { showPopupBanner, hidePopupBanner } from 'common/components/PopupBanner/popupBannerSlice';
import { setIsShowDialogOnLogin } from 'navigation/navigationSlice';
import useOnLoginSuccess from 'auth/hooks/useOnLoginSuccess';
import { getZendeskBaseLink } from 'utils/getZendeskLinks';
import { setUserProperties } from 'firebase/analytics';
import { getFbAnalytics } from 'utils/getFirebase';
import SsoButtons from '../../../auth/components/SsoButtons';
import {
  setLoginOtp,
  setLoginStep,
  setTempAccessToken,
  setIsEmailVerificationSuccess,
  resetLoginStep,
} from './authSlice';

const useStyles = createUseStyles(LoginStyles);

const {
  REFRESH_TOKEN_MAX_AGE,
  REMEMBER_ME_MAX_AGE,
} = config;

const ERROR_STATUS_TYPES = {
  SUSPENDED: 'UserIsSuspended',
  BANNED: 'UserIsBanned',
  DEACTIVATED: 'UserIsDeactivated',
};

const RELOAD_RETRIES = 15;

const Login = ({ location }) => {
  const classes = useStyles();
  const dispatch = useCakeDispatch();
  const history = useHistory();
  const recaptchaRef = useRef(null); // nosemgrep: typescript.react.security.audit.react-no-refs.react-no-refs
  const { state: errorState } = location;

  const [reCaptchaToken, setReCaptchaToken] = useState('');
  const [isRememberMe] = useState(true);
  const [isOtpRequired, setIsOtpRequired] = useState(false);
  const [isProcessingLogin, setIsProcessingLogin] = useState(false);
  const [accountStatus, setAccountStatus] = useState(errorState ? ERROR_STATUS_TYPES[errorState] : '');

  const { selectedLanguage } = useCakeSelector(state => state.languageRegionSettings);
  const { email, password, loginOtp: otp, loginStep, tempAccessToken, isEmailVerificationSuccess } = useCakeSelector(state => state.auth);
  const { checkAndRedeemVoucher } = useRedeem();
  const { onLoginSuccess, onPostLoginSuccess } = useOnLoginSuccess();

  const canLogin = isValidEmail(email) && !isEmpty(password);
  const canSubmitOtp = !isEmpty(otp) && otp.length === 6;
  const canLoginOtp = !isEmpty(email) && !isEmpty(password) && canSubmitOtp;

  // alternative OTP
  const [showAlternativeOTP, setShowAlternativeOTP] = useState(null);
  const [alternativeOTPMethod, setAlternativeOTPMethod] = useState(null);
  const [sentUsingAlternative, setSentUsingAlternative] = useState(false);
  const zeroPad = (num: number, places: number) => String(num).padStart(places, '0');
  const [timeLeft, { start: startTimer }] = useCountDown(1000 * 180); // 3 minutes
  const duration = moment.duration(timeLeft);
  const timeLeftMessage = timeLeft > 0
    ? `The code will expire in ${zeroPad(duration.minutes(), 2)}:${zeroPad(
      duration.seconds(),
      2,
    )}` : '';

  const handleSupportLinkClick = () => {
    window.open(getZendeskBaseLink(i18n.language), '_blank', 'noopener,noreferrer');
  };

  const handleAlternativeOTPClick = async () => {
    dispatch(setLoginStep(loginStep === 2 ? 3 : 2));
  };

  // previously in BasePage.js
  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const utmSource = query.get('utm_source');
    if (utmSource) {
      ['utm_source', 'utm_medium', 'utm_campaign'].forEach(utm => {
        storage.setItem(utm, query.get(utm));
      });
    }
    // reset location state
    if (errorState) {
      history.replace('/login', null);
    }
  }, [dispatch, history, errorState]);

  const recursiveReloadBalance = useCallback(async (initialCount) => {
    const [balancePromise] = await dispatch(reloadBalances()) as any;
    if (balancePromise.payload && !balancePromise.payload.length && initialCount <= RELOAD_RETRIES) {
      setTimeout(() => recursiveReloadBalance(initialCount + 1), 1000);
    }
  }, [dispatch]);

  const resetRecaptcha = useCallback(() => {
    try {
      setReCaptchaToken('');
      if (recaptchaRef?.current) {
        recaptchaRef.current.reset();
      }
    } catch (error) {
      throw new Error(`error resetting recaptcha ${reCaptchaToken}: ${error}`);
    }
  }, [reCaptchaToken]);

  const navigateAfterLogin = useCallback(() => {
    recursiveReloadBalance(0);
  }, [recursiveReloadBalance]);

  const requestAlternativeOtpCode = useCallback(async () => {
    window.cakepool.showSpinner();

    try {
      await sdk.alternativeOtp(tempAccessToken);
      startTimer();
      window.cakepool.showAlert(
        'success',
        `A verification code has been sent to your registered ${alternativeOTPMethod === 'SMS' ? 'phone number' : ' email address'}.`,
      );
      setSentUsingAlternative(true);
    } catch (err) {
      const { message } = err;
      window.cakepool.showAlert('error', message);
    } finally {
      window.cakepool.hideSpinner();
    }
  }, [tempAccessToken, alternativeOTPMethod, startTimer]);

  const otpLogin = useCallback(async () => {
    if (isProcessingLogin) return;

    window.cakepool.showSpinner();
    setIsProcessingLogin(true);

    try {
      const otpMethod = loginStep === 2 ? 'TOTP' : alternativeOTPMethod;
      await sdk.otpLogin(tempAccessToken, otp, otpMethod, isRememberMe);

      const options = {
        maxAge: REFRESH_TOKEN_MAX_AGE,
      };
      if (isRememberMe) {
        options.maxAge = REMEMBER_ME_MAX_AGE;
      }
      userTokenInfoStorage.set('isLoggedIn', true, options);
      dispatch(setIsShowDialogOnLogin(true));
      const user: any = await dispatch(reloadUser());
      onLoginSuccess(user); // single place to trigger other events after login success
      resetRecaptcha();
      await checkAndRedeemVoucher();
      setUserProperties(getFbAnalytics, {
        userId: user?.id,
        device_model: navigator.userAgent,
      });
      navigateAfterLogin();
      if (!user?.userDataConsent?.isDisplayConsentScreen) {
        onPostLoginSuccess();
      }
    } catch (err) {
      const { message, code } = err;
      // redirect to login if ExpiredToken
      if (code === 'ExpiredToken') {
        dispatch(setLoginStep(1));
        setIsOtpRequired(false);
      }
      let errMsg = <span><Trans>Unable to login</Trans>: <Trans>{message}</Trans></span>;
      if (
        typeof err.details !== 'undefined'
        && typeof err.details.loginAttempts !== 'undefined'
        && typeof err.details.maxAttempts !== 'undefined'
      ) {
        const { details: { maxAttempts, loginAttempts } } = err;
        const alertAttempts = maxAttempts - 3;
        if (loginAttempts >= alertAttempts) {
          const leftAttempts = maxAttempts - loginAttempts;
          errMsg = <span><Trans>Unable to login</Trans>: <Trans>{message}</Trans> (<Trans>{{ leftAttempts }} attempts left</Trans>)</span>;
        }
      }
      window.cakepool.showAlert('error', errMsg);
    } finally {
      await dispatch(setLoginOtp(''));
      setIsProcessingLogin(false);
      window.cakepool.hideSpinner();
    }
  }, [isProcessingLogin, tempAccessToken, otp, isRememberMe, dispatch, onLoginSuccess, checkAndRedeemVoucher, navigateAfterLogin, resetRecaptcha, loginStep, alternativeOTPMethod, onPostLoginSuccess]);

  const normalLogin = useCallback(async () => {
    if (isProcessingLogin) return;

    window.cakepool.showSpinner();
    setIsProcessingLogin(true);

    try {
      const result = await sdk.login(email, password, isRememberMe, reCaptchaToken);
      userTokenInfoStorage.set('isRememberMe', isRememberMe);
      const { token: accessToken, otpRequired, alternativeOtpMethod } = result;
      dispatch(setTempAccessToken(accessToken));
      setIsOtpRequired(otpRequired);
      setShowAlternativeOTP(!!alternativeOtpMethod);
      setAlternativeOTPMethod(alternativeOtpMethod);
      if (otpRequired) {
        dispatch(setLoginStep(2));
      } else {
        userTokenInfoStorage.set('isLoggedIn', true);
        dispatch(setIsShowDialogOnLogin(true));
        const user: any = await dispatch(reloadUser());
        onLoginSuccess(user); // single place to trigger other events after login success
        resetRecaptcha();
        sdk.UserInfoApi.updateLastActive();
        await checkAndRedeemVoucher();
        navigateAfterLogin();
        if (!user?.userDataConsent?.isDisplayConsentScreen) {
          onPostLoginSuccess();
        }
      }
    } catch (err) {
      dispatch(setLoginStep(1));
      const { message, code } = err;
      if (code === 'UnverifiedEmail') {
        userTokenInfoStorage.set('signUpEmail', email);
        history.push('/email-verification/email/true');
      } else if (Object.values(ERROR_STATUS_TYPES).includes(code)) { // show CTA Page if code is UserIsSuspended, UserIsBanned, UserIsDeactivated
        setAccountStatus(code);
      } else {
        window.cakepool.showAlert('error', <span><Trans>Unable to login</Trans>: <Trans>{message}</Trans></span>);
      }
    } finally {
      resetRecaptcha();
      setIsProcessingLogin(false);
      window.cakepool.hideSpinner();
    }
  }, [isProcessingLogin, email, password, isRememberMe, reCaptchaToken, dispatch, onLoginSuccess, checkAndRedeemVoucher, navigateAfterLogin, history, resetRecaptcha, onPostLoginSuccess]);

  const onLogin = useCallback(async () => {
    try {
      if (isOtpRequired && !canLoginOtp) {
        return;
      }
      if (!canLogin) {
        window.cakepool.showAlert('error', <Trans>Please fill required fields.</Trans>);
        return;
      }
      await recaptchaRef?.current.submit();
      if (reCaptchaToken) {
        if (isOtpRequired) {
          if (!isEmpty(otp) && otp.length === 6) {
            await otpLogin();
          }
        } else {
          await normalLogin();
        }
      }
    } catch (error) {
      throw new Error(`error with ${reCaptchaToken}: ${error}`);
    }
  }, [canLogin, canLoginOtp, isOtpRequired, normalLogin, otpLogin, reCaptchaToken, otp]);

  const hidePopupBannerAndLogin = useCallback(() => {
    dispatch(hidePopupBanner());
    onLogin();
  }, [dispatch, onLogin]);

  useEffect(() => {
    if (isEmailVerificationSuccess) {
      dispatch(showPopupBanner({
        title: 'Your email address has been confirmed.',
        buttonLabel: 'Continue',
        iconName: 'mail',
        buttonCallback: hidePopupBannerAndLogin,
        isActive: true,
      }));
      dispatch(setIsEmailVerificationSuccess(false));
      dispatch(setLoginStep(1));
    }
  }, [isEmailVerificationSuccess, dispatch, hidePopupBannerAndLogin]);

  useEffect(() => () => {
    dispatch(resetLoginStep());
    // dismiss the popup banner when page unmount
    dispatch(hidePopupBanner());
  }, []); // eslint-disable-line

  useEffect(() => {
    if (reCaptchaToken) {
      onLogin();
    }
  }, [onLogin, reCaptchaToken]);

  useEffect(() => {
    // try auto submitting
    if (!reCaptchaToken && !isEmpty(otp) && otp.length === 6) {
      otpLogin();
    }
  }, [otp, reCaptchaToken]); // eslint-disable-line

  const renderTitle = useMemo(() => {
    if (isProcessingLogin) {
      return 'Processing...';
    }
    if (loginStep === 1) {
      return 'Login';
    }
    if (loginStep === 2) {
      return 'Welcome back.';
    }
    if (loginStep === 3) {
      return `Enter ${alternativeOTPMethod === 'SMS' ? 'SMS' : 'Email'} Code`;
    }
    return '';
  }, [loginStep, isProcessingLogin, alternativeOTPMethod]);

  const isSubmitDisabled = () => {
    if (isProcessingLogin) {
      return true;
    }
    return isOtpRequired ? !canLoginOtp : !canLogin;
  };

  const submitButtonLabel = () => {
    if (isProcessingLogin) {
      return 'Logging in...';
    }
    return loginStep === 1 ? 'Continue' : 'Login';
  };

  const submitButton = (
    <ClickableElement
      style={{ width: '100%' }}
      minimumDelay={2000}
      onClick={() => !isSubmitDisabled() && onLogin()}
    >
      <Button
        outline
        type="button"
        tabIndex={4}
        disabled={isSubmitDisabled()}
        primary
        block
      ><Trans>{submitButtonLabel()}</Trans></Button>
    </ClickableElement>
  );

  const forgetPasswordButton = isProcessingLogin ? '' : (
    <Link to="/forget-password" className="mb-3 mr-4" >
      <Button
        outline
        primary
        clear
        tabIndex={5}
      >
        <Trans>Forgot password?</Trans>
      </Button>
    </Link>
  );

  const registerButton = isProcessingLogin ? '' : (
    <Link to="/register">
      <Button
        outline
        primary
        clear
        tabIndex={6}
      >
        <Trans>Sign up</Trans>
      </Button>
    </Link>
  );

  const form = (
    <form>
      {loginStep === 1 && <EmailPasswordInput onLogin={onLogin} />}
      {loginStep === 2 && <OTPInput isSubmitAutoTriggering />}
      {loginStep === 3
        && <div>
          <p>{`A 6-digit verification code will be sent to your registered ${alternativeOTPMethod === 'SMS' ? 'phone number' : 'email address'}.`}</p>
          <div className={classes.alternativeOtpMethodsContainer}>
            <div className={classes.alternativeOtpInputContainer}>
              <OTPInput isSubmitAutoTriggering showHelperText={false} />
            </div>
            <div>
              <Button primary onClick={requestAlternativeOtpCode} disabled={timeLeft > 0}>
                <Trans>{sentUsingAlternative ? 'RESEND' : 'SEND'}</Trans>
              </Button>
            </div>
          </div>
        </div>
      }
      {submitButton}
      { (loginStep === 3 && timeLeftMessage)
        && <p className='mt-24'>
          <Trans>Didn’t get a verification code?</Trans><br/>
          <Trans>{timeLeftMessage}</Trans>
        </p>
      }
      {loginStep === 1 && <>
        <div className="flex-horizontal-center mt-24 mb-24">
          <div>{forgetPasswordButton}</div>
          <div>{registerButton}</div>
        </div>
        <SsoButtons islogin={true}/>
      </>}
      <InvisibleRecaptchaV2 // nosemgrep: typescript.react.security.audit.react-no-refs.react-no-refs
        ref={recaptchaRef}
        onReCaptchaComplete={token => setReCaptchaToken(token)}
        selectedLanguage={selectedLanguage}
        level='regular'
      />
    </form>
  );

  if (Object.values(ERROR_STATUS_TYPES).includes(accountStatus)) {
    return (
      <Row className="justify-content-center">
        <Col md="5">
          <ErrorPage status={accountStatus} />
        </Col>
      </Row>
    );
  }

  return (
    <Row className="justify-content-center">
      <Col md="4">
        <article className={`floating ${classes.container}`}>
          <h1 className={classes.title}><Trans>{renderTitle}</Trans></h1>
          <div className="margin-bottom-40">
            { form }
            { (showAlternativeOTP && (loginStep === 2 || loginStep === 3))
              && <>
                <hr />
                <div className={classes.alternativeOtpMethods}>
                  <p><Trans>Or</Trans></p>
                  <Button primary medium block onClick={handleAlternativeOTPClick}>
                    <Trans>{loginStep === 2 ? 'Try Another Method' : 'Try Previous Method'}</Trans>
                  </Button>
                  <Button primary medium block onClick={handleSupportLinkClick}>
                    <Trans>Need Help? Contact Us</Trans>
                  </Button>
                </div>
              </>
            }
          </div>
        </article>
      </Col>
    </Row>
  );
};

export default Login;
